import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Logo from '../../Image/Logo.jpg';

import { warehouseService } from '../../services/warehouseService';
import ResponsiveSelect from '../common/ResponsiveSelect';
import NotificationBell from '../common/NotificationBell';

// Computed initials from email: split on '.'/whitespace/'_'/'-', use the
// first letter of the first two parts (uppercased), falling back to 'U'.
function getInitials(email?: string): string {
  if (!email) return 'U';
  const parts = email.split(/[.\s_-]+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const initials = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
  return initials || 'U';
}

export default function Navbar() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { user, setActiveWarehouse } = useAuth();

  const { data: warehouses } = useQuery({
    queryKey: ['navbar', 'warehouses'],
    queryFn: () => warehouseService.getWarehouses({ page: 1, limit: 100, status: 'active' }),
    enabled: !!user?.canAccessAllWarehouses,
    staleTime: 0,
    refetchOnMount: true,
  });

  const handleWarehouseChange = (warehouseId: number) => {
    setActiveWarehouse(warehouseId);
    queryClient.invalidateQueries();
  };

  return (
    <header className="relative z-40 shrink-0 bg-brand-900 text-white shadow-bar">
      <div className="h-[58px] lg:h-[62px] px-3 sm:px-4 lg:px-5 flex items-center justify-between gap-3">
        {/* Left: brand + mobile menu */}
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            onClick={() => window.dispatchEvent(new Event('sidebarMobileOpen'))}
            className="lg:hidden w-10 h-10 rounded-md bg-white/10 hover:bg-white/15 text-white flex items-center justify-center transition-colors"
            aria-label="Open menu"
          >
            <i className="fas fa-bars text-sm"></i>
          </button>

          <div className="flex items-center gap-2.5 min-w-0">
            <div className="hidden sm:flex h-9 w-9 rounded-md bg-white p-1 shrink-0">
              <img src={Logo} alt="Logo" className="h-full w-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] sm:text-sm font-extrabold tracking-tight truncate leading-tight">
                Smart WMS
              </p>
              <p className="text-[10px] sm:text-[11px] text-white/55 font-medium tracking-[0.12em] uppercase truncate">
                Control Console
              </p>
            </div>
          </div>
        </div>

        {/* Center: context chips */}
        <div className="hidden md:flex items-center gap-2 min-w-0">
          {user?.tenant && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/8 border border-white/10 max-w-[280px]">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/45 leading-none mb-0.5">
                  {user.isImpersonating ? 'Impersonating' : 'Tenant'}
                </p>
                <div className="flex items-center gap-1.5 min-w-0">
                  <p className="text-xs font-semibold truncate text-white/95">
                    {user.tenant.name} (#{user.tenantId})
                  </p>
                  {user.isImpersonating && (
                    <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-accent-100 text-accent-800">
                      Super Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {user && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/8 border border-white/10">
              <i className="fas fa-warehouse text-accent-400 text-xs"></i>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/45 leading-none mb-0.5">
                  Warehouse
                </p>
                {user.canAccessAllWarehouses ? (
                  <ResponsiveSelect
                    options={(warehouses?.items ?? []).map((warehouse: any) => ({ value: warehouse.id, label: warehouse.name }))}
                    value={user.activeWarehouseId ?? ''}
                    onChange={(val) => handleWarehouseChange(Number(val))}
                    includeEmpty={false}
                    ariaLabel="Active warehouse"
                    className="mt-0.5 min-w-[170px] rounded border-0 bg-transparent px-0 py-0 text-xs font-semibold text-white [&>option]:text-ink-900"
                  />
                ) : (
                  <p className="text-xs font-semibold truncate text-white/95">
                    {user.warehouse?.name || `WH #${user.activeWarehouseId ?? '-'}`}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: user */}
        <div className="flex items-center gap-2.5 shrink-0">
          <NotificationBell />
          <div className="hidden sm:block text-right max-w-[180px]">
            <p className="text-xs font-semibold truncate text-white/95">{user?.email}</p>
            <p className="text-[11px] text-white/50 capitalize truncate">{user?.role?.name}</p>
          </div>
          <div
            className="w-9 h-9 rounded-md bg-accent-500 text-brand-950 flex items-center justify-center text-xs font-extrabold shadow-soft"
            title={user?.email}
          >
            {getInitials(user?.email)}
          </div>
        </div>
      </div>

      {/* Mobile / tablet - compact Tenant and Warehouse Context, dashboard only */}
      {user && location.pathname === '/dashboard' && (
        <div className="md:hidden px-3 sm:px-4 pb-2.5 flex flex-wrap items-center gap-2">
          {user.tenant && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/8 border border-white/10 min-w-0">
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">
                {user.isImpersonating ? 'Impersonating' : 'Tenant'}
              </span>
              <span className="text-xs font-semibold text-white/95 truncate">
                {user.tenant.name} (#{user.tenantId})
              </span>
              {user.isImpersonating && (
                <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-accent-100 text-accent-800">
                  Super Admin
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/8 border border-white/10 min-w-0">
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">
              Warehouse
            </span>
            {user.canAccessAllWarehouses ? (
              <ResponsiveSelect
                options={(warehouses?.items ?? []).map((warehouse: any) => ({ value: warehouse.id, label: warehouse.name }))}
                value={user.activeWarehouseId ?? ''}
                onChange={(val) => handleWarehouseChange(Number(val))}
                includeEmpty={false}
                ariaLabel="Active warehouse"
                className="min-w-[140px] rounded border-0 bg-transparent px-0 py-0 text-xs font-semibold text-white [&>option]:text-ink-900"
              />
            ) : (
              <span className="text-xs font-semibold text-white/95 truncate">
                {user.warehouse?.name || `WH #${user.activeWarehouseId ?? '-'}`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Amber accent line */}
      <div className="h-0.5 bg-gradient-to-r from-accent-500 via-accent-400 to-accent-600" />
    </header>
  );
}
